import React, { Fragment } from 'react';
import {
  Box,
  Divider,
  FormLabel,
  Slider,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

/**
 * Componente para renderizar una pregunta de tipo suma constante
 * Permite al usuario distribuir puntos entre varias opciones
 * 
 * @param {Object} props
 * @param {number} props.questionId - ID único de la pregunta
 * @param {string} props.typeQuestion - Tipo de la pregunta
 * @param {string} props.questionName - Texto de la pregunta
 * @param {string} [props.description] - Descripción adicional de la pregunta
 * @param {number} props.index - Índice de la pregunta en el array
 * @param {Array<number>} props.unansweredQuestions - Array de índices de preguntas sin responder
 * @param {Object} props.questionRef - Ref para scroll automático
 * @param {Array<Object>} props.options - Opciones disponibles para asignar puntos
 * @param {string} props.options[].optionName - Nombre de la opción
 * @param {number} props.score - Puntaje total a distribuir
 * @param {Object} props.formValue - Valor actual del formulario para esta pregunta
 * @param {Object} props.formValue.sliders - Objeto con valores asignados {optionName: value}
 * @param {Function} props.onChange - Callback cuando cambia un slider
 * @param {boolean} props.submitAttempted - Si se intentó enviar el formulario
 * @param {Object} props.sliderErrors - Objeto con errores de validación
 * @param {string} [props.sliderErrors[index]] - Mensaje de error para esta pregunta
 * @param {boolean} [props.showDivider=true] - Si se debe mostrar el divisor al final
 */
const ConstantSumQuestion = ({
  questionId,
  typeQuestion,
  questionName,
  description,
  index,
  unansweredQuestions = [],
  questionRef,
  options = [],
  score,
  formValue = {},
  onChange,
  submitAttempted = false,
  sliderErrors = {},
  showDivider = true,
  hasOwnBold,
}) => {
  const hasError = unansweredQuestions.includes(index);
  const sliders = formValue.sliders || {};

  // Calcular totales
  const totalAsignado = Object.values(sliders).reduce((a, b) => a + b, 0);
  const restante = score - totalAsignado;

  // Determinar color del slider según el restante
  const getSliderColor = () => {
    if (restante === 0) return 'success'; // verde
    if (restante < 0) return 'error'; // rojo
    return 'primary'; // por defecto
  };

  return (
    <Fragment>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <FormLabel
            id={`${questionId}-${typeQuestion}`}
            style={{
              fontSize: '1.1',
              fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
              marginBottom: '0.8em',
              color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
            }}
            ref={questionRef}
          >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
          </FormLabel>

          {description && (
            <Typography
              variant="caption"
              style={{ display: 'block', fontStyle: 'italic' }}
            >
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
            </Typography>
          )}
        </Box>

        {/* Cuadros de resumen */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box
            sx={{
              flex: 1,
              backgroundColor: '#e3f2fd',
              padding: 0.5,
              borderRadius: 2,
              border: '1px solid #90caf9',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Asignados
            </Typography>
            <Typography variant="caption">
              {totalAsignado} / {score}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              backgroundColor: '#fce4ec',
              padding: 0.5,
              borderRadius: 2,
              border: '1px solid #f48fb1',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Restantes
            </Typography>
            <Typography variant="caption">{restante}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {options.map(({ optionName }) => {
          const currentValue = sliders[optionName] ?? 0;
          const sliderColor = getSliderColor();

          return (
            <Box key={optionName} sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                {optionName}: {currentValue}
              </Typography>
              <Slider
                value={currentValue}
                min={0}
                max={score}
                step={1}
                onChange={(e, newValue) =>
                  onChange(newValue, index, optionName)
                }
                valueLabelDisplay="auto"
                color={sliderColor}
              />
            </Box>
          );
        })}
      </Box>

      {submitAttempted && sliderErrors[index] && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {sliderErrors[index]}
        </Typography>
      )}

      {showDivider && <Divider variant="middle" />}
    </Fragment>
  );
};

export default ConstantSumQuestion;